import { PasswordEditor, validators } from '@approvalmax/ui';
import { ui } from 'modules/common';
import { TwoFaActionWizard } from 'modules/components';
import { useDispatch, useSelector } from 'modules/react-redux';
import { LOGIN_PAGE_ID } from 'pages/auth/config';
import { FC, memo, useCallback, useState } from 'react';
import { notificationService } from 'services/notification';

import {
    finishResetPassword,
    sendResetPasswordTFAEmailCode,
    showLoginModule,
    verifyResetPasswordTFACode,
} from '../../actions';
import { PasswordResetFinalStepModuleData } from '../../reducers/page/moduleReducer';
import { getModule } from '../../selectors/pageSelectors';
import { messages } from './PasswordResetFinalStepModule.messages';
import { Buttons, Card, PasswordField, SubmitButton, Title } from './PasswordResetFinalStepModule.styles';

const PasswordResetFinalStepModule: FC = memo(() => {
    const dispatch = useDispatch();

    const { tfaEnabled, isTFAAlternativeEmailEnabled, isTFABackupCodesEnabled } = useSelector((state) =>
        getModule<PasswordResetFinalStepModuleData>(state)
    );

    const [twoFaPassed, setTwoFaPassed] = useState(!tfaEnabled);
    const [newPassword, setNewPassword] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onSetNewPassword = useCallback(async () => {
        if (validators.password(newPassword)) {
            setShowErrors(true);

            if (!newPassword) {
                notificationService.showErrorToast(messages.emptypasswordError);
            } else {
                notificationService.showErrorToast(messages.invalidPasswordError);
            }

            return;
        }

        try {
            setIsLoading(true);
            await dispatch(finishResetPassword(newPassword));
        } catch {
            setIsLoading(false);
        }
    }, [dispatch, newPassword]);

    const onEnterCode = useCallback(
        async (userCode: string) => {
            await dispatch(verifyResetPasswordTFACode(userCode));
            setTwoFaPassed(true);
        },
        [dispatch]
    );
    const onSendCodeViaEmail = useCallback(async () => {
        await dispatch(sendResetPasswordTFAEmailCode());
    }, [dispatch]);

    const onClose = useCallback(() => {
        dispatch(
            showLoginModule({
                innerPageId: LOGIN_PAGE_ID,
            })
        );
    }, [dispatch]);

    if (!twoFaPassed) {
        return (
            <TwoFaActionWizard
                title={messages.title}
                open
                onEnterAppCode={onEnterCode}
                onEnterBackupCode={onEnterCode}
                onEnterEmailCode={onEnterCode}
                onSendCodeViaEmail={onSendCodeViaEmail}
                isTFAAlternativeEmailEnabled={isTFAAlternativeEmailEnabled}
                isTFABackupCodesEnabled={isTFABackupCodesEnabled}
                onClose={onClose}
                disableAutoClose
            />
        );
    }

    return (
        <div>
            <Card>
                <Title>{messages.title}</Title>

                <PasswordField title={messages.newPasswordTitle} required>
                    <PasswordEditor
                        value={newPassword}
                        onChange={(value) => {
                            setShowErrors(true);
                            setNewPassword(value);
                        }}
                    />
                </PasswordField>

                <ui.PasswordValidationSection password={newPassword} highlightInvalidCriteria={showErrors} />

                <Buttons>
                    <SubmitButton execute={onSetNewPassword} disabled={isLoading}>
                        {messages.setPasswordButtonText}
                    </SubmitButton>
                </Buttons>
            </Card>
        </div>
    );
});

export default PasswordResetFinalStepModule;
